import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext, useParams, useNavigate } from 'react-router-dom'
import { useI18n } from 'gunsi-auth'
import { useTally, useRangeTransactions } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { formatAmount, formatDate, expandAllSlices } from '../format.js'
import { Emoji } from '../components/Emoji.jsx'
import { CategoryIcon } from '../components/CategoryIcon.jsx'
import { MonthPicker } from '../components/MonthPicker.jsx'
import { DatePicker } from '../components/DatePicker.jsx'
import {
  shiftLilMonth, lilMonthRange, formatLilMonthJsx, formatLilYearJsx,
  lilToHe, heToLil, todayLil,
} from '../lil.jsx'
import { pscObjectToString, pscToGregorian } from '@justhyur/lil-calendar'

const PALETTE = [
  '#ef5350', '#ff7043', '#ffa726', '#ffd54f', '#ffee58',
  '#d4e157', '#9ccc65', '#66bb6a', '#26a69a', '#26c6da',
  '#42a5f5', '#5c6bc0', '#7e57c2', '#ab47bc', '#ec407a',
]

function shiftYearMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
function todayYearMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
const pad2 = (n) => String(n).padStart(2, '0')
function isoToday() {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}
function isoMonthStart() {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`
}
// Exclusive upper bound = the day after `iso` (YYYY-MM-DD).
function nextDayIso(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  const nd = new Date(y, m - 1, d + 1)
  return `${nd.getFullYear()}-${pad2(nd.getMonth() + 1)}-${pad2(nd.getDate())}`
}
function formatDayDisplay(iso, locale, dateFormat) {
  if (!iso) return '—'
  return formatDate(iso, locale, dateFormat)
}
function formatYearMonth(ym, locale) {
  const [y, m] = ym.split('-').map(Number)
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  return new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' })
    .format(new Date(y, m - 1, 1))
}

export function Stats() {
  const t = useTallyI18n()
  const { locale } = useI18n()
  const { subTopbar } = useOutletContext()
  const {
    data,
    viewedGreg: viewedMonth, setViewedGreg: setViewedMonth,
    viewedLil, setViewedLil,
  } = useTally()
  const navigate = useNavigate()
  const params = useParams()
  const isLil   = data.calendar === 'lil'
  const yearEra = data.yearEra ?? 'lil'

  const [tab, setTab]               = useState('expense') // 'income' | 'expense'
  // Init from the URL so the state matches the address on mount. Otherwise the
  // reflect-to-URL effect would fire with a stale 'monthly' before the parse
  // effect sets the real period, ping-ponging the URL into an infinite loop
  // (e.g. landing on /stats/<year> via back → blank screen / freeze).
  const [period, setPeriod]         = useState(() => {
    if (params.from && params.to) return 'custom'
    if (params.year === 'all') return 'all'
    if (params.year && !params.month) return 'yearly'
    return 'monthly'
  }) // 'monthly' | 'yearly' | 'all' | 'custom'
  const [pickerOpen, setPickerOpen]   = useState(false)

  // Custom-interval bounds (inclusive dates, YYYY-MM-DD). Prefer the URL (so a
  // deep-link / back matches the address and doesn't loop), then the persisted
  // per-device value, then defaults.
  const customInit = (() => {
    try { return JSON.parse(localStorage.getItem('gunsi_tally_stats_custom')) || {} }
    catch { return {} }
  })()
  const [customFrom, setCustomFrom] = useState(
    params.from ? String(params.from).slice(0, 10) : (customInit.from || isoMonthStart())
  )
  const [customTo,   setCustomTo]   = useState(
    params.to ? String(params.to).slice(0, 10) : (customInit.to || isoToday())
  )
  const [fromOpen, setFromOpen] = useState(false)
  const [toOpen,   setToOpen]   = useState(false)
  const fromAnchorRef = useRef(null)
  const toAnchorRef   = useRef(null)
  useEffect(() => {
    try { localStorage.setItem('gunsi_tally_stats_custom', JSON.stringify({ from: customFrom, to: customTo })) } catch { /* ignore */ }
  }, [customFrom, customTo])
  // Keep from ≤ to whenever either endpoint changes.
  function pickFrom(iso) { setCustomFrom(iso); if (iso > customTo) setCustomTo(iso) }
  function pickTo(iso)   { setCustomTo(iso);   if (iso < customFrom) setCustomFrom(iso) }
  // List display: 'tree' (accordion, default) or 'flat' (all rows spread out)
  const [listMode, setListMode] = useState(() => {
    try { return localStorage.getItem('gunsi_tally_stats_listmode') === 'flat' ? 'flat' : 'tree' }
    catch { return 'tree' }
  })
  useEffect(() => {
    try { localStorage.setItem('gunsi_tally_stats_listmode', listMode) } catch {}
  }, [listMode])
  // Show per-day averages instead of period totals (works for both monthly and
  // yearly). UI-only pref, persisted to localStorage.
  const [dailyMode, setDailyMode] = useState(() => {
    try { return localStorage.getItem('gunsi_tally_stats_dailymode') === '1' }
    catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('gunsi_tally_stats_dailymode', dailyMode ? '1' : '0') } catch {}
  }, [dailyMode])

  // ── URL ↔ state sync ─────────────────────────────────────────────────
  // URL pattern:
  //   /stats                    → defaults to current month
  //   /stats/<year>             → yearly view
  //   /stats/<year>/<month>     → monthly view; <month> is 1..12 (greg) /
  //                                1..13 or 'S' (lil)
  // <year> is in the active era — for Lil + yearEra='he' it's the HE year.
  useEffect(() => {
    if (params.from && params.to) {
      setPeriod('custom')
      setCustomFrom(String(params.from).slice(0, 10))
      setCustomTo(String(params.to).slice(0, 10))
      return
    }
    if (!params.year) return
    if (params.year === 'all') { setPeriod('all'); return }
    const y = Number(params.year)
    if (!Number.isFinite(y)) return
    if (params.month) {
      setPeriod('monthly')
      if (isLil) {
        const lilYear = yearEra === 'he' ? heToLil(y) : y
        if (params.month === 'S' || params.month === 's') {
          setViewedLil({ year: lilYear, special: true })
        } else {
          const m = Number(params.month)
          if (Number.isFinite(m) && m >= 1 && m <= 13) {
            setViewedLil({ year: lilYear, month: m })
          }
        }
      } else {
        const m = Number(params.month)
        if (Number.isFinite(m) && m >= 1 && m <= 12) {
          setViewedMonth(`${y}-${String(m).padStart(2, '0')}`)
        }
      }
    } else {
      setPeriod('yearly')
      if (isLil) {
        const lilYear = yearEra === 'he' ? heToLil(y) : y
        setViewedLil(v => ({ year: lilYear, month: v.month ?? 1 }))
      } else {
        setViewedMonth(v => `${y}-${v.split('-')[1] ?? '01'}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.year, params.month, params.from, params.to, isLil, yearEra])

  // Reflect state into URL (replace so back-button navigates *out* of /stats,
  // not through every prior period).
  useEffect(() => {
    let path
    if (period === 'custom') {
      path = `/stats/custom/${customFrom}/${customTo}`
    } else if (period === 'all') {
      path = '/stats/all'
    } else if (period === 'yearly') {
      const y = isLil
        ? (yearEra === 'he' ? lilToHe(viewedLil.year) : viewedLil.year)
        : Number(viewedMonth.split('-')[0])
      path = `/stats/${y}`
    } else if (isLil) {
      const y = yearEra === 'he' ? lilToHe(viewedLil.year) : viewedLil.year
      const m = viewedLil.special ? 'S' : viewedLil.month
      path = `/stats/${y}/${m}`
    } else {
      const [y, m] = viewedMonth.split('-').map(Number)
      path = `/stats/${y}/${m}`
    }
    if (window.location.pathname !== path) {
      navigate(path, { replace: true })
    }
  }, [period, viewedMonth, viewedLil, isLil, yearEra, customFrom, customTo, navigate])

  // Range [from, to) for the active period — always greg ISO dates
  const { range, cacheKey } = useMemo(() => {
    if (period === 'custom') {
      // Inclusive [customFrom, customTo] → half-open [from, nextDay(to)).
      return {
        range: { from: customFrom, to: nextDayIso(customTo) },
        cacheKey: `custom:${customFrom}:${customTo}`,
      }
    }
    if (period === 'all') {
      // Everything up to today (data capped later via effectiveTo). A wide
      // static range keeps the cacheKey stable (no daily churn).
      return { range: { from: '1900-01-01', to: '9999-12-31' }, cacheKey: 'all' }
    }
    if (period === 'yearly') {
      if (isLil) {
        const y = viewedLil.year
        const from = pscToGregorian(pscObjectToString({ year: y, month: 1, day: 1 }))
        const to   = pscToGregorian(pscObjectToString({ year: y + 1, month: 1, day: 1 }))
        return { range: { from, to }, cacheKey: `lilY:${y}` }
      }
      const y = Number(viewedMonth.split('-')[0])
      return { range: { from: `${y}-01-01`, to: `${y + 1}-01-01` }, cacheKey: `gregY:${y}` }
    }
    // monthly
    if (isLil) {
      const r = lilMonthRange(viewedLil)
      return {
        range: r,
        cacheKey: `lil:${viewedLil.year}-${viewedLil.special ? 'S' : viewedLil.month}`,
      }
    }
    const [y, m] = viewedMonth.split('-').map(Number)
    const from = `${y}-${String(m).padStart(2, '0')}-01`
    const to   = m === 12
      ? `${y + 1}-01-01`
      : `${y}-${String(m + 1).padStart(2, '0')}-01`
    return { range: { from, to }, cacheKey: `greg:${viewedMonth}` }
  }, [period, isLil, viewedMonth, viewedLil, customFrom, customTo])

  const { transactions, loading } = useRangeTransactions(cacheKey, range.from, range.to)

  // Today's window (ISO YYYY-MM-DD, local). `tomorrowIso` is the exclusive
  // upper bound "up to and including today".
  const { todayIso, tomorrowIso } = useMemo(() => {
    const n = new Date()
    const f = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      todayIso:    f(new Date(n.getFullYear(), n.getMonth(), n.getDate())),
      tomorrowIso: f(new Date(n.getFullYear(), n.getMonth(), n.getDate() + 1)),
    }
  }, [])

  // Never show future-dated data: cap the period's exclusive end at "tomorrow".
  // Used both for filtering tx and for the daily-average divisor. A custom
  // interval is an explicit user choice, so it's respected as-is (no cap).
  const effectiveTo = useMemo(() => {
    const rawTo = String(range.to).slice(0, 10)
    if (period === 'custom') return rawTo
    return rawTo < tomorrowIso ? rawTo : tomorrowIso
  }, [range.to, tomorrowIso, period])

  // Arrow/swipe navigation only applies to the month/year views. Allow "next"
  // only when the following period has already started (its start ≤ today).
  const navigable = period === 'monthly' || period === 'yearly'
  const canGoNext = navigable && String(range.to).slice(0, 10) <= todayIso
  const canGoPrev = navigable

  // The month/year view is shared with Transactions (where navigating into the
  // future IS allowed). When landing on Stats with an entirely-future period
  // (its start is after today), clamp to the current one — the nearest valid
  // period. Skipped for 'all'/'custom'. Only fires when out of range → no loop.
  useEffect(() => {
    if (!navigable) return
    if (String(range.from).slice(0, 10) > todayIso) {
      if (isLil) setViewedLil(todayLil())
      else       setViewedMonth(todayYearMonth())
    }
  }, [navigable, range.from, todayIso, isLil, setViewedLil, setViewedMonth])

  // ── Swipe between months/years (same pattern as Transactions) ────────
  const stripRef       = useRef(null)
  const startXRef      = useRef(null)
  const startYRef      = useRef(null)
  const directionRef   = useRef(null)
  const currentDxRef   = useRef(0)
  const busyRef        = useRef(false)
  const lastNavDirRef  = useRef(null) // 'next' | 'prev'
  const navStateRef    = useRef({ isLil, period, canGoNext })
  useEffect(() => { navStateRef.current = { isLil, period, canGoNext } }, [isLil, period, canGoNext])
  const ANIM_MS = 150

  function shiftViewed(delta) {
    if (delta > 0 ? !canGoNext : !canGoPrev) return
    lastNavDirRef.current = delta > 0 ? 'next' : 'prev'
    if (period === 'yearly') {
      if (isLil) setViewedLil(v => ({ year: v.year + delta, month: v.month ?? 1 }))
      else {
        const [y, m] = viewedMonth.split('-').map(Number)
        setViewedMonth(`${y + delta}-${String(m).padStart(2, '0')}`)
      }
    } else {
      if (isLil) setViewedLil(v => shiftLilMonth(v, delta))
      else       setViewedMonth(v => shiftYearMonth(v, delta))
    }
  }

  // Slide-in animation when the cacheKey changes (= viewed period changed)
  useLayoutEffect(() => {
    const strip = stripRef.current
    if (!strip) return
    const dir = lastNavDirRef.current
    if (!dir) return
    lastNavDirRef.current = null
    const w = strip.offsetWidth || window.innerWidth
    strip.style.transition = 'none'
    strip.style.transform  = `translateX(${dir === 'next' ? w : -w}px)`
    void strip.offsetWidth
    strip.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
    strip.style.transform  = 'translateX(0)'
  }, [cacheKey])

  // Touch swipe (mobile)
  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return

    function onTouchStart(e) {
      if (busyRef.current) return
      startXRef.current = e.touches[0].clientX
      startYRef.current = e.touches[0].clientY
      directionRef.current = null
      currentDxRef.current = 0
      strip.style.transition = 'none'
    }
    function onTouchMove(e) {
      if (startXRef.current == null || busyRef.current) return
      const touch = e.touches[0]
      const dx = touch.clientX - startXRef.current
      const dy = touch.clientY - startYRef.current
      if (directionRef.current === null) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          directionRef.current = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v'
        }
        return
      }
      if (directionRef.current === 'h') {
        if (e.cancelable) e.preventDefault()
        currentDxRef.current = dx
        strip.style.transform = `translateX(${dx}px)`
      }
    }
    function onTouchEnd() {
      if (startXRef.current == null || busyRef.current) return
      const dx = currentDxRef.current
      startXRef.current = null
      currentDxRef.current = 0
      const goNext = dx < 0
      // Bounce back on: not-a-swipe, non-navigable mode (all/custom), or a
      // forward swipe that would enter a future period.
      const per = navStateRef.current.period
      const blocked = per === 'all' || per === 'custom'
        || (goNext && !navStateRef.current.canGoNext)
      if (directionRef.current !== 'h' || Math.abs(dx) < 50 || blocked) {
        strip.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
        strip.style.transform = 'translateX(0)'
        return
      }
      busyRef.current = true
      const w = strip.offsetWidth || window.innerWidth
      strip.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
      strip.style.transform  = `translateX(${goNext ? -w : w}px)`
      setTimeout(() => {
        const { isLil: il, period: per } = navStateRef.current
        const delta = goNext ? +1 : -1
        lastNavDirRef.current = goNext ? 'next' : 'prev'
        if (per === 'yearly') {
          if (il) setViewedLil(v => ({ year: v.year + delta, month: v.month ?? 1 }))
          else    setViewedMonth(v => {
            const [y, m] = v.split('-').map(Number)
            return `${y + delta}-${String(m).padStart(2, '0')}`
          })
        } else {
          if (il) setViewedLil(v => shiftLilMonth(v, delta))
          else    setViewedMonth(v => shiftYearMonth(v, delta))
        }
        busyRef.current = false
      }, ANIM_MS)
    }

    strip.addEventListener('touchstart', onTouchStart, { passive: true })
    strip.addEventListener('touchmove',  onTouchMove,  { passive: false })
    strip.addEventListener('touchend',   onTouchEnd)
    return () => {
      strip.removeEventListener('touchstart', onTouchStart)
      strip.removeEventListener('touchmove',  onTouchMove)
      strip.removeEventListener('touchend',   onTouchEnd)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  function jumpToMonth(target) {
    if (isLil) setViewedLil(target)
    else       setViewedMonth(target)
  }

  function navigateToCategoryDetail(catId) {
    let yr, mo
    if (isLil) {
      yr = yearEra === 'he' ? lilToHe(viewedLil.year) : viewedLil.year
      mo = viewedLil.special ? 'S' : viewedLil.month
    } else {
      const [y, m] = viewedMonth.split('-').map(Number)
      yr = y; mo = m
    }
    // Carry the current period so the detail opens matching it (yearly stays
    // yearly instead of snapping back to monthly). Drill-down is disabled for
    // all/custom, so period here is only 'monthly' | 'yearly'.
    navigate(`/stats/${yr}/${mo}/cat/${catId}`, { state: { period } })
  }

  const periodLabel = useMemo(() => {
    if (period === 'custom') return '' // custom shows date pickers, not this label
    if (period === 'all') return t.stats.all
    if (period === 'yearly') {
      const y = isLil ? viewedLil.year : Number(viewedMonth.split('-')[0])
      return isLil ? formatLilYearJsx(y, yearEra) : String(y)
    }
    if (isLil) {
      if (viewedLil.special) {
        return <>{formatLilYearJsx(viewedLil.year, yearEra)} – {formatLilYearJsx(viewedLil.year + 1, yearEra)}</>
      }
      return formatLilMonthJsx(viewedLil, yearEra)
    }
    return formatYearMonth(viewedMonth, locale)
  }, [period, isLil, yearEra, viewedMonth, viewedLil, locale, t])

  // Expand split tx into per-slice rows, then keep only slices that fall in
  // the viewed [range.from, effectiveTo). A split parent can be returned by the
  // backend's overlap query for any month it touches; we want each slice to
  // count in the month of its own effective date, not the parent's whole
  // amount in every month. `effectiveTo` also excludes future-dated tx.
  const expandedTx = useMemo(() => {
    const flat = expandAllSlices(transactions)
    const from = String(range.from).slice(0, 10)
    if (!from || !effectiveTo) return flat
    return flat.filter(tx => {
      const d = (tx.effectiveDate || tx.date || '').slice(0, 10)
      return d >= from && d < effectiveTo
    })
  }, [transactions, range.from, effectiveTo])

  // Days to divide by for the daily-average view. For a bounded period → its
  // elapsed length (capped at today). For 'all' → from the earliest tx to
  // today, so the average spans the actual data history.
  const dayCount = useMemo(() => {
    const MS = 86400000
    let startIso
    if (period === 'all') {
      let min = null
      for (const tx of expandedTx) {
        const d = (tx.effectiveDate || tx.date || '').slice(0, 10)
        if (d && (min === null || d < min)) min = d
      }
      startIso = min ?? effectiveTo
    } else {
      startIso = String(range.from).slice(0, 10)
    }
    const start = new Date(startIso + 'T00:00:00')
    const end   = new Date(effectiveTo + 'T00:00:00')
    return Math.max(1, Math.round((end - start) / MS))
  }, [period, range.from, effectiveTo, expandedTx])
  const perDay = (v) => (dailyMode ? v / dayCount : v)

  // Totali (in default currency via amount_base) — on expanded slices
  const { incomeTotal, expenseTotal } = useMemo(() => {
    let inc = 0, exp = 0
    for (const tx of expandedTx) {
      const v = tx.amount_base ?? tx.amount ?? 0
      if (tx.type === 'income')  inc += v
      if (tx.type === 'expense') exp += v
    }
    return { incomeTotal: inc, expenseTotal: exp }
  }, [expandedTx])

  // Totals per category leaf + Lista flat sotto il chart — on expanded slices
  const totalByCatId = useMemo(() => {
    const m = new Map()
    for (const tx of expandedTx) {
      if (tx.type !== tab) continue
      const v = tx.amount_base ?? tx.amount ?? 0
      const id = tx.categoryId || '__none__'
      m.set(id, (m.get(id) ?? 0) + v)
    }
    return m
  }, [expandedTx, tab])

  // Anelli per il sunburst (vedi buildRings). Indicizza per parent/child.
  const rings = useMemo(
    () => buildRings(data.categories, totalByCatId, t.stats.misc),
    [data.categories, totalByCatId, t.stats.misc],
  )

  // Map<rootCatId, colorHex> from the sunburst's outer ring (so list badges
  // match the chart slices).
  const rootColorMap = useMemo(() => ringRootColorMap(rings), [rings])

  // Lista flat (categorie che hanno tx dirette, ordinata desc) — affianco al chart
  const rows = useMemo(() => {
    const arr = []
    totalByCatId.forEach((total, id) => {
      const cat = data.categories.find(c => c.id === id)
      arr.push({
        id,
        name: cat?.name ?? '—',
        icon: cat?.icon ?? (tab === 'income' ? '💰' : '💸'),
        source: cat?.source,   // categorie di sistema: icona col marchio dell'app
        type: cat?.type,
        total,
      })
    })
    arr.sort((a, b) => b.total - a.total)
    arr.forEach(r => {
      const root = findRootId(r.id, data.categories)
      r.color = rootColorMap.get(root) ?? '#888'
    })
    return arr
  }, [totalByCatId, data.categories, tab, rootColorMap])

  // Tree (accordion view): roots with aggregated totals, sub-totals on expand.
  const tree = useMemo(() => {
    const childrenOf = new Map()
    for (const c of data.categories) {
      if (c.type !== tab) continue
      const pid = c.parentId ?? null
      if (!childrenOf.has(pid)) childrenOf.set(pid, [])
      childrenOf.get(pid).push(c)
    }
    function subtreeTotal(catId) {
      let s = totalByCatId.get(catId) ?? 0
      for (const c of childrenOf.get(catId) ?? []) s += subtreeTotal(c.id)
      return s
    }
    function build(cat) {
      const total = subtreeTotal(cat.id)
      if (total <= 0) return null
      const kids = (childrenOf.get(cat.id) ?? [])
        .map(build).filter(Boolean)
        .sort((a, b) => b.total - a.total)
      return {
        id: cat.id,
        name: cat.name,
        icon: cat.icon ?? (tab === 'income' ? '💰' : '💸'),
        source: cat.source,
        type: cat.type,
        total,
        own: totalByCatId.get(cat.id) ?? 0,
        children: kids,
      }
    }
    const roots = (childrenOf.get(null) ?? [])
      .map(build).filter(Boolean)
      .sort((a, b) => b.total - a.total)
    const uncat = totalByCatId.get('__none__') ?? 0
    if (uncat > 0) {
      roots.push({
        id: '__none__',
        name: '—',
        icon: tab === 'income' ? '💰' : '💸',
        total: uncat, own: uncat, children: [],
      })
    }
    return roots
  }, [data.categories, totalByCatId, tab])

  const total = tab === 'income' ? incomeTotal : expenseTotal
  const monthPickerVisible = pickerOpen && period === 'monthly'

  const stickyContent = (
    <div className="stats-sticky-head">
      {/* Tabs Expense / Income (cat-toggle style) with totals */}
      <div className="cat-toggle stats-tabs" role="tablist">
        <button
          type="button"
          className={`tx-totals-toggle${dailyMode ? ' is-active' : ''}`}
          onClick={() => setDailyMode(v => !v)}
          title={dailyMode ? t.stats.periodTotalsHint : t.stats.dailyAvgHint}
          aria-label={dailyMode ? t.stats.periodTotalsHint : t.stats.dailyAvgHint}
          aria-pressed={dailyMode}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="8" y1="2" x2="8" y2="5" />
            <line x1="16" y1="2" x2="16" y2="5" />
            <circle cx="12" cy="15" r="1.6" fill="currentColor" stroke="none" />
          </svg>
        </button>
        <button
          type="button"
          role="tab"
          className={`cat-toggle-btn expense${tab === 'expense' ? ' active' : ''}`}
          onClick={() => setTab('expense')}
        >
          <span className="stats-tab-label">
            {t.dashboard.expenses}{dailyMode && <span className="tx-total-suffix">{t.stats.perDay}</span>}
          </span>
          <span className="stats-tab-val">{formatAmount(perDay(expenseTotal), data.currency)}</span>
        </button>
        <button
          type="button"
          role="tab"
          className={`cat-toggle-btn income${tab === 'income' ? ' active' : ''}`}
          onClick={() => setTab('income')}
        >
          <span className="stats-tab-label">
            {t.dashboard.income}{dailyMode && <span className="tx-total-suffix">{t.stats.perDay}</span>}
          </span>
          <span className="stats-tab-val">{formatAmount(perDay(incomeTotal), data.currency)}</span>
        </button>
      </div>

      {/* Layout: period-select (left) · month-nav/custom (center) · list-mode (right) */}
      <header className="view-header view-header-month">
        <select
          className="period-select"
          value={period}
          onChange={e => setPeriod(e.target.value)}
        >
          <option value="monthly">{t.stats.monthly}</option>
          <option value="yearly">{t.stats.yearly}</option>
          <option value="all">{t.stats.all}</option>
          <option value="custom">{t.stats.custom}</option>
        </select>

        {period === 'all' ? (
          // "All" needs no period navigator — keep the center cell empty.
          <span aria-hidden="true" />
        ) : period === 'custom' ? (
          <div className="stats-custom-range">
            <button
              type="button"
              ref={fromAnchorRef}
              className="month-nav-label stats-range-btn"
              onClick={() => setFromOpen(true)}
            >
              {formatDayDisplay(customFrom, locale, data.dateFormat)}
            </button>
            <span className="stats-range-sep" aria-hidden="true">→</span>
            <button
              type="button"
              ref={toAnchorRef}
              className="month-nav-label stats-range-btn"
              onClick={() => setToOpen(true)}
            >
              {formatDayDisplay(customTo, locale, data.dateFormat)}
            </button>
          </div>
        ) : (
          <div className="month-nav">
            <button
              className="month-nav-arrow"
              onClick={() => shiftViewed(-1)}
              disabled={!canGoPrev}
              aria-label="Previous"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className={`month-nav-label${period === 'monthly' && isLil && viewedLil.special ? ' is-range' : ''}`}
              onClick={() => period === 'monthly' && setPickerOpen(true)}
            >
              {periodLabel}
            </button>
            <button
              className="month-nav-arrow"
              onClick={() => shiftViewed(+1)}
              disabled={!canGoNext}
              aria-label="Next"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        )}

        <button
          type="button"
          className="stats-listmode-toggle"
          onClick={() => setListMode(m => m === 'tree' ? 'flat' : 'tree')}
          aria-label={listMode === 'tree' ? 'Vista piatta' : 'Vista albero'}
          title={listMode === 'tree' ? 'Vista piatta' : 'Vista albero'}
        >
          {listMode === 'tree' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 6h6" /><path d="M4 12h10" /><path d="M4 18h8" /><path d="M4 4v16" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          )}
        </button>
      </header>
    </div>
  )

  return (
    <>
      {subTopbar && createPortal(stickyContent, subTopbar)}
      <div className="view view-stats">
        <div className="stats-strip" ref={stripRef}>
        {loading ? (
          <div className="view-empty"><div className="loading-spinner" /></div>
        ) : rows.length === 0 || total <= 0 ? (
          <div className="view-empty"><span>{t.stats.empty}</span></div>
        ) : (
          <>
            <Sunburst rings={rings} inverted={listMode === 'flat'} />
            <div className="stats-list">
            {(listMode === 'flat' ? rows : tree).map(r => {
              const pct = total > 0 ? (r.total / total) * 100 : 0
              const color = listMode === 'flat'
                ? r.color
                : (rootColorMap.get(r.id) ?? '#888')
              const isReal = r.id && r.id !== '__none__'
              // Category detail is a per-month view, so it's not meaningful in
              // the all-time / custom-interval aggregates — disable it there.
              const clickable = isReal && period !== 'all' && period !== 'custom'
              return (
                <div
                  key={r.id}
                  className={`stats-row${clickable ? ' clickable' : ''}`}
                  onClick={clickable ? () => navigateToCategoryDetail(r.id) : undefined}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                >
                  <span className="stats-pct" style={{ background: color }}>
                    {pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%
                  </span>
                  <span className="cat-icon"><CategoryIcon category={r} size="1.3rem" /></span>
                  <span className="stats-name">{r.name}</span>
                  <span className={`stats-val ${tab === 'income' ? 'amount-income' : 'amount-expense'}`}>
                    {formatAmount(perDay(r.total), data.currency)}
                  </span>
                </div>
              )
            })}
            </div>
          </>
        )}
        </div>

        <MonthPicker
          open={monthPickerVisible}
          onClose={() => setPickerOpen(false)}
          mode={isLil ? 'lil' : 'greg'}
          viewed={isLil ? viewedLil : viewedMonth}
          onSelect={jumpToMonth}
          yearEra={yearEra}
          locale={locale}
          t={t}
          disableFuture
        />

        {/* Custom-interval date pickers (only mounted in custom mode) */}
        <DatePicker
          open={fromOpen}
          value={customFrom}
          onChange={pickFrom}
          onClose={() => setFromOpen(false)}
          anchorRef={fromAnchorRef}
          isLilAvailable={isLil}
          yearEra={yearEra}
          locale={locale}
          t={t}
        />
        <DatePicker
          open={toOpen}
          value={customTo}
          onChange={pickTo}
          onClose={() => setToOpen(false)}
          anchorRef={toAnchorRef}
          isLilAvailable={isLil}
          yearEra={yearEra}
          locale={locale}
          t={t}
        />
      </div>
    </>
  )
}

function Sunburst({ rings, inverted = false }) {
  // Native-SVG sunburst — Recharts 3 has a bug stacking multiple <Pie>s.
  // `inverted` is now driven by the parent (single toggle that also flips
  // the breakdown list between tree/flat).
  const N = rings.length
  if (N === 0) return null

  // When inverted, render rings in reverse order — the deepest level becomes
  // the outermost, so its labels show.
  const orderedRings = inverted ? [...rings].reverse() : rings

  // ViewBox wider than the pie itself so outer labels never spill out.
  // Labels are now capped at ~13 chars (≈75px wide) so the side padding can
  // be tighter — the chart itself grows.
  const OUTER = 110
  const LBL_GAP = 16
  const PAD_X   = 78
  const PAD_Y   = 22
  const SIZE_W  = 2 * (OUTER + LBL_GAP + PAD_X)
  const SIZE_H  = 2 * (OUTER + LBL_GAP + PAD_Y)
  const CX = SIZE_W / 2, CY = SIZE_H / 2
  // Outer ring has a fixed standard width; deeper rings get progressively
  // thinner (as fractions of the outer width). The donut's inner radius
  // grows as more rings are added — outer ring is never resized.
  const OUTER_W = 30
  const INNER_WEIGHTS = [0.65, 0.5, 0.4]
  let cur = OUTER
  const radii = []
  radii.push({ outer: cur, inner: cur - OUTER_W + 1 })
  cur -= OUTER_W
  for (let i = 0; i < N - 1; i++) {
    const w = OUTER_W * (INNER_WEIGHTS[i] ?? 0.35)
    radii.push({ outer: cur, inner: cur - w + 1 })
    cur -= w
  }

  // Compute the angular range of each slice per ring (DFS order)
  const slicesPerRing = orderedRings.map((data) => {
    const total = data.reduce((s, d) => s + (d.v ?? 0), 0)
    let acc = 0
    return data.map(d => {
      const startFrac = total > 0 ? acc / total : 0
      acc += d.v ?? 0
      const endFrac = total > 0 ? acc / total : 0
      return { ...d, startFrac, endFrac, total }
    })
  })

  // Outer-ring labels: name + percent for slices ≥ 3%
  const outer = slicesPerRing[0] ?? []
  const labelRadius = OUTER + 16

  return (
    <div className="stats-pie-wrap">
      <svg viewBox={`0 0 ${SIZE_W} ${SIZE_H}`} className="sunburst-svg" width="100%" preserveAspectRatio="xMidYMid meet">
        {slicesPerRing.map((data, depth) => {
          const r = radii[depth]
          return (
            <g key={depth}>
              {data.map((d, i) => {
                const frac = d.endFrac - d.startFrac
                if (frac <= 0) return null
                const a0 = d.startFrac * 2 * Math.PI - Math.PI / 2
                const a1 = d.endFrac   * 2 * Math.PI - Math.PI / 2
                const dPath = arcPath(CX, CY, r.inner, r.outer, a0, a1)
                // Slice arc length (mid-radius): when too small, the 1.5px
                // stroke would visually swallow the fill — drop the stroke
                // for tiny slices so the colour stays visible.
                const arcLen = frac * 2 * Math.PI * ((r.outer + r.inner) / 2)
                const tooThin = arcLen < 2.5
                const stroke = d.ext || tooThin ? d.color : 'var(--g-bg)'
                const sw     = (d.ext || tooThin) ? 0 : 1
                return (
                  <path
                    key={d.id ?? i}
                    d={dPath}
                    fill={d.color}
                    fillRule="evenodd"
                    stroke={stroke}
                    strokeWidth={sw}
                  >
                    {d.name ? <title>{d.name}</title> : null}
                  </path>
                )
              })}
            </g>
          )
        })}
        {/* Outer ring labels with leader lines */}
        {outer.map((d, i) => {
          if (d.color === 'transparent') return null
          const pct = d.endFrac - d.startFrac
          if (pct < 0.03) return null
          const mid = (d.startFrac + d.endFrac) / 2
          const a = mid * 2 * Math.PI - Math.PI / 2
          const lx0 = CX + OUTER * Math.cos(a)
          const ly0 = CY + OUTER * Math.sin(a)
          const lx1 = CX + (OUTER + 8) * Math.cos(a)
          const ly1 = CY + (OUTER + 8) * Math.sin(a)
          const tx  = CX + labelRadius * Math.cos(a)
          const ty  = CY + labelRadius * Math.sin(a)
          const anchor = Math.cos(a) > 0.05 ? 'start' : Math.cos(a) < -0.05 ? 'end' : 'middle'
          const label = truncateLabel(d.name, 13)
          return (
            <g key={`lbl-${i}`} className="sunburst-label">
              <line
                x1={lx0} y1={ly0} x2={lx1} y2={ly1}
                stroke="var(--g-text-muted)" strokeWidth="1"
              />
              <text x={tx} y={ty} textAnchor={anchor} fill="var(--g-text)" fontSize={11} fontWeight={600}>
                {label !== d.name && <title>{d.name}</title>}
                <tspan>{label}</tspan>
                <tspan x={tx} dy={13} fill="var(--g-text-muted)" fontWeight={500}>
                  {(pct * 100).toFixed(1)}%
                </tspan>
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

function truncateLabel(s, max) {
  if (!s) return s
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

function arcPath(cx, cy, rIn, rOut, a0, a1) {
  const sweep = a1 - a0
  // Full circle (single 100% slice) — SVG can't draw an arc from X back to X,
  // so emit two half-arcs (outer) + two reversed half-arcs (inner) and rely
  // on fill-rule:evenodd to punch out the hole.
  if (sweep >= 2 * Math.PI - 1e-6) {
    const outerR = `${rOut} ${rOut}`
    if (rIn <= 0) {
      return `M ${cx + rOut} ${cy} A ${outerR} 0 1 1 ${cx - rOut} ${cy} A ${outerR} 0 1 1 ${cx + rOut} ${cy} Z`
    }
    const innerR = `${rIn} ${rIn}`
    return [
      `M ${cx + rOut} ${cy}`,
      `A ${outerR} 0 1 1 ${cx - rOut} ${cy}`,
      `A ${outerR} 0 1 1 ${cx + rOut} ${cy}`,
      `Z`,
      `M ${cx + rIn} ${cy}`,
      `A ${innerR} 0 1 0 ${cx - rIn} ${cy}`,
      `A ${innerR} 0 1 0 ${cx + rIn} ${cy}`,
      `Z`,
    ].join(' ')
  }
  const large = sweep > Math.PI ? 1 : 0
  const x0o = cx + rOut * Math.cos(a0), y0o = cy + rOut * Math.sin(a0)
  const x1o = cx + rOut * Math.cos(a1), y1o = cy + rOut * Math.sin(a1)
  const x0i = cx + rIn  * Math.cos(a0), y0i = cy + rIn  * Math.sin(a0)
  const x1i = cx + rIn  * Math.cos(a1), y1i = cy + rIn  * Math.sin(a1)
  if (rIn <= 0) {
    return `M ${cx} ${cy} L ${x0o.toFixed(2)} ${y0o.toFixed(2)} A ${rOut} ${rOut} 0 ${large} 1 ${x1o.toFixed(2)} ${y1o.toFixed(2)} Z`
  }
  return `M ${x0o.toFixed(2)} ${y0o.toFixed(2)}
          A ${rOut} ${rOut} 0 ${large} 1 ${x1o.toFixed(2)} ${y1o.toFixed(2)}
          L ${x1i.toFixed(2)} ${y1i.toFixed(2)}
          A ${rIn} ${rIn} 0 ${large} 0 ${x0i.toFixed(2)} ${y0i.toFixed(2)}
          Z`
}

// ── Sunburst data builder ───────────────────────────────────────────────
// • Ring count = max depth of the actual category tree (only branches with
//   transactions are counted). So if no sub-categories are involved → 1 ring.
// • A leaf that ends before maxDepth extends its slice into the deeper rings
//   with the same colour (visual continuation — looks like the parent slice
//   "drops down" through the unused rings).
// • A parent with both children AND own direct tx gets a "Varie" filler in
//   the next ring, same colour as the parent — angularly aligned, looks
//   continuous, but the tooltip says "Health · Varie".
function buildRings(categories, totalByCatId, miscLabel = 'Misc.') {
  const HARD_CAP = 4
  const catById = new Map(categories.map(c => [c.id, c]))
  const childrenOf = new Map()
  for (const c of categories) {
    const pid = c.parentId ?? null
    if (!childrenOf.has(pid)) childrenOf.set(pid, [])
    childrenOf.get(pid).push(c)
  }

  function subtreeTotal(catId) {
    const own = totalByCatId.get(catId) ?? 0
    const kids = childrenOf.get(catId) ?? []
    let s = own
    for (const c of kids) s += subtreeTotal(c.id)
    return s
  }

  // Max depth among branches that actually have tx
  function depthOf(catId) {
    const kidsWith = (childrenOf.get(catId) ?? []).filter(c => subtreeTotal(c.id) > 0)
    if (kidsWith.length === 0) return 0
    let m = 0
    for (const c of kidsWith) m = Math.max(m, 1 + depthOf(c.id))
    // A parent with own direct tx + children needs an extra ring for the
    // "Varie" filler (slice + its visual extension)
    const own = totalByCatId.get(catId) ?? 0
    if (own > 0) m = Math.max(m, 1)
    return m
  }

  const roots = (childrenOf.get(null) ?? []).filter(r => subtreeTotal(r.id) > 0)
  let treeDepth = 0
  for (const r of roots) treeDepth = Math.max(treeDepth, depthOf(r.id))
  const RINGS_COUNT = Math.min(HARD_CAP, treeDepth + 1)

  for (const list of childrenOf.values()) {
    list.sort((a, b) => subtreeTotal(b.id) - subtreeTotal(a.id))
  }

  const rings = Array.from({ length: RINGS_COUNT }, () => [])

  function extendDown(value, startDepth, id, color) {
    for (let d = startDepth; d < RINGS_COUNT; d++) {
      rings[d].push({ id: `${id}@${d}::ext`, name: '', v: value, color, ext: true })
    }
  }

  function walk(catId, depth, color) {
    const own = totalByCatId.get(catId) ?? 0
    const kids = (childrenOf.get(catId) ?? []).filter(c => subtreeTotal(c.id) > 0)
    const subtree = subtreeTotal(catId)
    if (subtree === 0) return
    const cat = catById.get(catId)
    rings[depth].push({
      id: `${catId}@${depth}`,
      name: cat?.name ?? '—',
      v: subtree,
      color,
    })
    if (depth + 1 >= RINGS_COUNT) return
    if (kids.length === 0) {
      // Leaf — visually continue the slice through the deeper rings
      extendDown(subtree, depth + 1, catId, color)
      return
    }
    for (const c of kids) {
      walk(c.id, depth + 1, mixHex(color, '#ffffff', 0.18))
    }
    if (own > 0) {
      // Visible "Varie" filler — same colour as parent (visual continuation)
      rings[depth + 1].push({
        id: `${catId}@${depth + 1}::own`,
        name: `${cat?.name ?? '—'} · ${miscLabel}`,
        v: own,
        color,
      })
      if (depth + 2 < RINGS_COUNT) {
        extendDown(own, depth + 2, `${catId}__own`, color)
      }
    }
  }

  roots.sort((a, b) => subtreeTotal(b.id) - subtreeTotal(a.id))
  let rootIdx = 0
  for (const r of roots) {
    walk(r.id, 0, PALETTE[rootIdx % PALETTE.length])
    rootIdx++
  }

  // Uncategorized bucket (tx without categoryId) — slice + continuation
  const uncat = totalByCatId.get('__none__') ?? 0
  if (uncat > 0) {
    const color = '#6b7280'
    rings[0].push({ id: '__none__@0', name: '—', v: uncat, color })
    extendDown(uncat, 1, '__none__', color)
  }

  return rings
}

// Helpers for the per-row colour in the flat list — maps any cat id to its
// root colour (so the badge in the list matches the outer ring slice).
function ringRootColorMap(rings) {
  const m = new Map()
  const outer = rings[0]
  if (!outer) return m
  for (const slice of outer) {
    const baseId = slice.id.split('@')[0]
    m.set(baseId, slice.color)
  }
  return m
}
function findRootId(catId, categories) {
  let cur = categories.find(c => c.id === catId)
  while (cur && cur.parentId) {
    cur = categories.find(c => c.id === cur.parentId)
  }
  return cur?.id ?? catId
}

// Mix two hex colours: amount=0 → c1, amount=1 → c2
function mixHex(c1, c2, amount) {
  const p = (h) => {
    const x = h.replace('#', '')
    return [parseInt(x.slice(0, 2), 16), parseInt(x.slice(2, 4), 16), parseInt(x.slice(4, 6), 16)]
  }
  const [r1, g1, b1] = p(c1)
  const [r2, g2, b2] = p(c2)
  const r = Math.round(r1 + (r2 - r1) * amount)
  const g = Math.round(g1 + (g2 - g1) * amount)
  const b = Math.round(b1 + (b2 - b1) * amount)
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')}`
}
